/**
 * 화면(병영·편성·합류 장면)에 병사·영웅 그림을 싣는다. 전장과 같은 층 그림(`public/sprites/rim.png`)을 차림마다 구운 서기.
 *
 * 작은 그림은 구운 칸 하나를 CSS 배경으로 잘라 보인다 — 줄마다 캔버스를 만들면 병영 수십 줄에서 무겁다.
 * 도트는 배율 1(칸 한 픽셀 = CSS 한 픽셀)로만 보인다. 0.5배처럼 줄이면 픽셀이 뭉개져 얼굴이 사라졌다.
 */
import { lookFor, resolve } from '../render/looks.ts';
import { RIM_TOP, bakeLook, lookHands, lookKey, parseLook } from '../render/rim-bake.ts';
import { RIM_MANIFEST } from '../render/rim-manifest.ts';
import { loadAtlas } from '../render/rim-sheet.ts';
import { SHIELD_CELL, ShieldView, loadShieldImage, shieldCellAt } from '../render/shields.ts';
import type { CombatantSpec, Culture } from '../sim/troops.ts';

/**
 * 이 인물의 작은 그림 열쇠: 차림 열쇠(장수·동료는 착장을 따른다). 전장(`UnitSheet.keyFor`)과 같은 규칙이다.
 *
 * 방패를 들었으면 열쇠 끝에 방패 칸(`나라` + 큰 방패면 `b`)이 하나 더 붙는다. 전장은 방패를 인물과 따로 그리지만(부서지면
 * 방패만 사라진다) 화면의 그림은 한 장이라 여기서 얹는다 — 얹지 않았더니 병사 상세를 봐도 방패를 든 병사인지 알 수
 * 없었다(플레이한 사람: "방패가 있다는 사실을 알 수 없어", "캐릭터 모습에서도 방패를 그려줘"). 차림이 같아도 방패가
 * 다르면 다른 그림이므로 열쇠가 갈려야 한다.
 */
export function iconKeyFor(spec: CombatantSpec): string {
  const look = lookKey(lookFor(spec));
  return (spec.shield ?? 0) > 0 ? `${look},${spec.culture}${spec.shieldBig === true ? 'b' : ''}` : look;
}

/** 방패 칸을 뗀 열쇠(차림만). */
export function bareKey(key: string): string {
  return key.split(',').slice(0, 6).join(',');
}

/** 열쇠 끝의 방패 칸(`iconKeyFor`). 없으면 null. */
export function shieldOfKey(key: string): { culture: Culture; big: boolean } | null {
  const f = key.split(',')[6];
  if (!f) return null;
  const culture = Number(f[0]);
  if (culture !== 0 && culture !== 1 && culture !== 2) return null;
  return { culture, big: f.endsWith('b') };
}

/**
 * 구운 칸(가로 `RIM_MANIFEST.cell`, 위에 `RIM_TOP` 여백)에서 방패 그림의 왼쪽 위. 전장(`Renderer.syncShield`)과 같은 손
 * 자리(`lookHands`)에 방패 칸의 가운데를 둔다 — 손은 발 자리(칸 가운데, 발 아래 줄)에서 잰 값이다.
 */
export function shieldSpot(slot: number, horse?: string): { x: number; y: number } {
  const hand = lookHands(RIM_MANIFEST, horse).stand[slot];
  const half = SHIELD_CELL >> 1;
  return { x: (RIM_MANIFEST.cell >> 1) + hand.dx - half, y: RIM_MANIFEST.feet + 1 + RIM_TOP + hand.dy - half };
}

/**
 * 차림마다 서기 한 칸을 한 번 구워 둔다(병영 수십 줄이 같은 병종이면 한 번만 굽는다). `top`은 무기를 뺀 모습(머리·투구·관)의
 * 맨 윗 불투명 줄 — 자르기 창이 이보다 아래에서 시작하면 창을 올린다. 창의 세로 자리를 고정값으로 두었더니 말 탄 장수의
 * 관모·금관이 카드와 초상에서 잘렸다(리뷰·QA가 짚었다). 무기를 빼고 재는 것은 창끝까지 넣으면 창수 카드가 창끝에 맞춰 밀려서다.
 */
interface RimStand {
  cell: HTMLCanvasElement;
  top: number;
  /** 무기를 뺀 모습(말 탄 인물은 말까지)의 불투명 테두리 — `whole` 창이 이것에 맞춘다. 그림이 비었으면 칸 전체. */
  left: number;
  right: number;
  bottom: number;
  /** 방패를 들었는데 방패 그림을 못 받아 인물만 구웠다. 보이기는 하되 붙들어 두지 않는다(`remember`). */
  lacking: boolean;
}

/**
 * 만든 것을 열쇠로 붙들어 두되, 다 만든 것(`whole`)만 붙든다. 못 만든 것(null·던짐)과 덜 만든 것은 한 번 돌려주고
 * 잊는다 — 다음에 다시 만든다.
 *
 * 던진 약속을 붙들어 영영 빈 그림이었던 적이 있고(리뷰), 방패를 얹으면서는 방패 그림만 못 받은 칸(인물만 구운 것)을
 * 다 만든 것으로 붙들어, 방패 그림을 나중에 받아도 그 병종의 그림에는 끝내 방패가 없었다(리뷰가 브라우저로 보였다).
 */
export function remember<T>(map: Map<string, Promise<T | null>>, key: string, make: () => Promise<T | null>, whole: (v: T) => boolean): Promise<T | null> {
  let p = map.get(key);
  if (!p) {
    const made = make().catch(() => null);
    p = made;
    map.set(key, made);
    void made.then((v) => {
      if ((v === null || !whole(v)) && map.get(key) === made) map.delete(key);
    });
  }
  return p;
}

const rimStand = new Map<string, Promise<RimStand | null>>();

function opaqueBox(c: HTMLCanvasElement): { top: number; left: number; right: number; bottom: number } {
  const g = c.getContext('2d', { willReadFrequently: true })!;
  const d = g.getImageData(0, 0, c.width, c.height).data;
  let top = c.height;
  let left = c.width;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < c.height; y++) {
    for (let x = 0; x < c.width; x++) {
      if (d[(y * c.width + x) * 4 + 3] < 128) continue;
      if (y < top) top = y;
      if (x < left) left = x;
      if (x > right) right = x;
      bottom = y;
    }
  }
  return right < 0 ? { top: c.height, left: 0, right: c.width - 1, bottom: c.height - 1 } : { top, left, right, bottom };
}

function rimStandCell(key: string): Promise<RimStand | null> {
  return remember(rimStand, key, () => {
    const worn = shieldOfKey(key);
    return Promise.all([loadAtlas(), worn ? loadShieldImage() : null]).then(([img, shields]): RimStand | null => {
      if (!img) return null;
      const n = RIM_MANIFEST.cell;
      const look = resolve(parseLook(key), RIM_MANIFEST);
      const strip = bakeLook(img, RIM_MANIFEST, look);
      // 말 탄 인물은 왼쪽 보기 서기(칸 1) — 정면은 말 머리가 사람 앞이라 작은 그림에서 말 얼굴만 보였다.
      const slot = look.horse ? 1 : 0;
      const cut = (from: HTMLCanvasElement): HTMLCanvasElement => {
        const c = document.createElement('canvas');
        c.width = n;
        c.height = n + RIM_TOP;
        c.getContext('2d', { willReadFrequently: true })!.drawImage(from, slot * n, 0, n, n + RIM_TOP, 0, 0, n, n + RIM_TOP);
        return c;
      };
      const bare = cut(bakeLook(img, RIM_MANIFEST, { ...look, weapon: '' }));
      const cell = cut(strip);
      // 방패는 몸과 무기 위에 얹는다(앞·왼쪽 보기는 전장에서도 방패가 몸 앞이다). 방패 그림을 못 받았으면 방패 없이 —
      // 인물 그림까지 비우지 않는다. 테두리(`bare`)에는 넣지 않는다: 창의 자리가 방패 쪽으로 밀리면 같은 줄의 다른
      // 병사와 몸이 어긋난다.
      if (worn && shields) {
        const from = shieldCellAt(worn.culture, worn.big, ShieldView.Front);
        const at = shieldSpot(slot, look.horse);
        cell.getContext('2d', { willReadFrequently: true })!.drawImage(shields, from.x, from.y, SHIELD_CELL, SHIELD_CELL, at.x, at.y, SHIELD_CELL, SHIELD_CELL);
      }
      return { cell, ...opaqueBox(bare), lacking: worn !== null && !shields };
    });
  }, (s) => !s.lacking);
}

const rimUrl = new Map<string, Promise<{ url: string; lacking: boolean } | null>>();

function rimStandUrl(key: string): Promise<string | null> {
  return remember(
    rimUrl,
    key,
    () => rimStandCell(key).then((s) => (s ? { url: s.cell.toDataURL('image/png'), lacking: s.lacking } : null)),
    (u) => !u.lacking,
  ).then((u) => u?.url ?? null);
}

/**
 * 잘라 볼 창(여백 `RIM_TOP`을 뺀 64칸 기준): 발(52줄)과 척추(가운데)를 기준으로. full은 전신, bust는 머리부터 허리까지.
 *
 * 보병은 키가 30픽셀 남짓이라 정수리가 21~25줄, 얼굴이 30~38줄쯤이다. 처음에는 bust를 16줄부터 잘라 위 여섯 줄이
 * 빈 칸이었고 턱 아래가 잘려, 폰에서 편성 칩의 병사가 얼굴이 잘려 보였다(플레이한 사람이 짚었다).
 */
const CROP = {
  full: { x: 14, y: 14, w: 36, h: 40 },
  bust: { x: 20, y: 21, w: 24, h: 24 },
} as const;

/**
 * 림월드식 말 탄 인물(왼쪽 보기 서기)의 창. 기수는 안장 위로 올라가 머리가 칸의 6~22줄에 있다 — 보병 창(14줄부터)을 그대로
 * 써서 기수 머리 윗 절반이 잘렸다(리뷰가 짚었다). 말 다리 아래 몇 줄은 잘려도 된다. 크기는 보병 창과 같다(카드 줄이 흔들리지 않게).
 */
const RIM_RIDER = {
  full: { x: 13, y: 3, w: 36, h: 40 },
  bust: { x: 27, y: 3, w: 24, h: 24 },
} as const;

/**
 * `whole`: 머리부터 발(말 탄 인물은 말발굽)까지 다 들어오는 창. 출전 위치 표의 부대장 그림에 쓴다.
 *
 * `full`은 이름과 달리 말 탄 인물을 다 담지 못한다 — 카드 줄이 흔들리지 않게 보병 창(36×40)과 같은 크기로 두어서, 폭 42에
 * 높이 44쯤인 말 탄 장수는 말 엉덩이와 다리가 잘린다. 출전 위치 표에서 플레이한 사람이 짚었다: "부대장 캐릭터 아이콘이 잘 안
 * 보여. 아이콘에 캐릭터 전신이 보이게 고쳐줘." 창의 자리를 고정값이 아니라 **구운 그림의 테두리**에서 정한다(가로는 가운데,
 * 세로는 발에 맞춤) — 보병과 말 탄 인물이 같은 크기의 창에서 같은 바닥줄에 선다.
 *
 * 높이 56은 가장 큰 몸에 맞춘 것이다: 군마에 조우관을 쓰면 정수리(깃털 끝)부터 발굽까지 54줄이다. 처음에는 48이었고 창의
 * 세로 자리가 머리를 보지 않아, 말 탄 인물이 투구·관을 쓰면 위가 4~9줄 잘렸다(리뷰와 QA가 짚었다 — 말 × 투구 384가지 가운데
 * 종장판주·금동관모·조우관은 전부).
 */
const WHOLE = { w: 46, h: 56 } as const;

/** 병사·영웅 한 명의 작은 그림. 굽는 동안이나 그림이 없으면 빈 자리(같은 크기)로 — 줄 모양이 흔들리지 않게. */
export function unitIcon(key: string | null, size: 'full' | 'bust' | 'whole' = 'full'): HTMLElement {
  // 가슴 위 그림에는 방패를 얹지 않는다: 손 자리가 창 밖이라 방패의 귀퉁이만 쐐기처럼 걸려 방패로 읽히지 않았다(QA).
  if (key && size === 'bust') key = bareKey(key);
  const c = size === 'whole' ? WHOLE : CROP[size];
  const rider = size === 'whole' ? null : RIM_RIDER[size];
  const span = document.createElement('span');
  span.className = `unit-icon ${size}`;
  span.setAttribute('aria-hidden', 'true');
  span.style.width = `${c.w}px`;
  span.style.height = `${c.h}px`;
  if (key && !rider) {
    void Promise.all([rimStandUrl(key), rimStandCell(key)]).then(([url, st]) => {
      if (!url || !st) return;
      const { x, y } = wholeWindow(st, st.cell.width, st.cell.height);
      span.style.backgroundImage = `url(${url})`;
      span.style.backgroundPosition = `-${x}px -${y}px`;
    });
  } else if (key && rider) {
    // 굽는 동안은 빈 자리(같은 크기), 구워지면 그 칸을 배경으로. 말 탄 차림은 기수 머리에 맞춘 창.
    // 구운 칸은 위에 RIM_TOP 여백이 있다 — 창의 세로 자리를 그만큼 내린다.
    const rc0 = key.split(',')[5] ? rider : CROP[size === 'bust' ? 'bust' : 'full'];
    void Promise.all([rimStandUrl(key), rimStandCell(key)]).then(([url, st]) => {
      if (!url || !st) return;
      // 머리·투구·관의 맨 윗줄이 창 위로 나가면 창을 그만큼 올린다(한 줄 여유).
      const y = Math.max(0, Math.min(rc0.y + RIM_TOP, st.top - 1));
      span.style.backgroundImage = `url(${url})`;
      span.style.backgroundPosition = `-${rc0.x}px -${y}px`;
    });
  }
  return span;
}

/**
 * `whole` 창의 왼쪽 위 자리. 가로는 몸(무기를 뺀 테두리)의 가운데, 세로는 발 아래 세 줄을 남기고 바닥에 맞춘다(세 줄은
 * 발보다 조금 아래로 내려오는 창대·칼끝의 몫). **머리와 발이 여백보다 먼저다**: 그렇게 두면 정수리가 창 위로 나가는 큰 몸은
 * 창을 올려 정수리 위 한 줄까지만 남기고, 그래도 다 못 담는 몸(창보다 큰 몸)은 머리를 지킨다 — 누구인지는 머리가 말한다.
 * 긴 창끝은 위로 삐져나가도 된다(테두리가 무기를 빼고 잰 것이다). 칸 밖으로는 나가지 않는다.
 */
export function wholeWindow(
  box: { left: number; right: number; top: number; bottom: number },
  cellW: number,
  cellH: number,
): { x: number; y: number } {
  const mid = (box.left + box.right + 1) >> 1;
  const x = Math.max(0, Math.min(cellW - WHOLE.w, mid - (WHOLE.w >> 1)));
  // 발 아래 세 줄 → 정수리가 잘리면 올린다(위 한 줄) → 그러다 발이 잘리면 발에 맞춘다 → 몸이 창보다 크면 머리에 맞춘다.
  const feet = box.bottom + 1 - WHOLE.h;
  const y0 = Math.max(feet, Math.min(box.bottom + 4 - WHOLE.h, box.top - 1));
  const y = Math.max(0, Math.min(cellH - WHOLE.h, Math.min(y0, box.top)));
  return { x, y };
}

/**
 * 큰 초상: 서기를 `scale`배로 캔버스에 그린다. 그림을 구운 뒤에 그리므로 비동기다. 받지 못하면 false이고 캔버스는
 * 건드리지 않는다 — 합류 장면은 먼저 그려 둔 코드 초상(`drawCompanionPortrait`)이 그대로 남는다.
 * 가슴 위가 아니라 전신 — 누가 무엇을 들고 오는지까지 보인다.
 */
export async function drawUnitPortrait(canvas: HTMLCanvasElement, key: string, scale = 4, whole = false): Promise<boolean> {
  const st = await rimStandCell(key);
  if (!st) return false;
  // `whole`: 머리부터 발(말발굽)까지 다 담는 창(`wholeWindow`) — 몸을 가로 가운데, 발을 바닥에 맞춘다. 고정 창(42×50)은
  // 군마에 조우관을 쓴 장수의 아래 다섯 줄을 잘랐고(리뷰가 쟀다: 몸이 54줄), 걸어 선 사람은 왼쪽 아래로 치우쳐 보였다(QA).
  const c = whole
    ? { ...wholeWindow(st, st.cell.width, st.cell.height), w: WHOLE.w, h: WHOLE.h }
    : { x: 11, y: Math.max(0, Math.min(6 + RIM_TOP, st.top - 1)), w: 42, h: 50 };
  canvas.width = c.w * scale;
  canvas.height = c.h * scale;
  const g = canvas.getContext('2d');
  if (!g) return false;
  g.imageSmoothingEnabled = false;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.drawImage(st.cell, c.x, c.y, c.w, c.h, 0, 0, canvas.width, canvas.height);
  return true;
}
